import { Bell, CalendarDays, Calculator, CircleUserRound, LayoutDashboard, ListFilter, Menu, PlugZap, Radar, ReceiptText, Search, Sparkles, Ticket, WalletCards, X } from 'lucide-react';
import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';

export type AppSection = 'dashboard' | 'my-opportunities' | 'ranking' | 'feed' | 'alerts' | 'market' | 'portfolio' | 'watchlist' | 'calendar' | 'profit' | 'intelligence' | 'providers' | 'accounts';

export const primaryNavigation: ReadonlyArray<readonly [AppSection, string, typeof LayoutDashboard]> = [
  ['dashboard', 'Přehled', LayoutDashboard],
  ['my-opportunities', 'My Opportunities', ListFilter],
  ['ranking', 'Top Opportunities', Sparkles],
  ['feed', 'Příležitosti', ListFilter],
  ['alerts', 'Upozornění', Bell],
  ['market', 'Market', Radar],
  ['portfolio', 'Portfolio', ReceiptText],
  ['watchlist', 'Watchlist', Ticket],
  ['calendar', 'Kalendář', CalendarDays],
  ['profit', 'Profit Lab', Calculator],
  ['intelligence', 'Intelligence', Sparkles],
  ['providers', 'Providers', PlugZap],
  ['accounts', 'Accounts / Nastavení', WalletCards],
];

const navigationGroups: ReadonlyArray<readonly [string, readonly AppSection[]]> = [
  ['Command', ['dashboard', 'my-opportunities']],
  ['Intelligence', ['ranking', 'feed', 'alerts', 'market', 'intelligence']],
  ['Operations', ['portfolio', 'watchlist', 'calendar', 'profit']],
  ['System', ['providers', 'accounts']],
];

const titles: Record<AppSection, string> = {
  dashboard: 'Přehled', 'my-opportunities': 'My Opportunities', ranking: 'Top Opportunities', feed: 'Opportunity Feed', alerts: 'Upozornění', market: 'Ticketmaster Market', portfolio: 'Portfolio', watchlist: 'Watchlist',
  calendar: 'Kalendář', profit: 'Profit Lab', intelligence: 'Intelligence', providers: 'Providers', accounts: 'Accounts / Nastavení',
};

export function AppShell({ children, active, onNavigate, onNewPurchase, onSearch }: { children: ReactNode; active: AppSection; onNavigate: (section: AppSection) => void; onNewPurchase: () => void; onSearch?:()=>void }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const drawerRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const pageTitleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => { pageTitleRef.current?.focus({ preventScroll: true }); }, [active]);

  useEffect(() => {
    if (!menuOpen) return;
    const drawer = drawerRef.current;
    const focusable = drawer?.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    focusable?.[0]?.focus();
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.body.classList.add('scroll-locked');
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.classList.remove('scroll-locked');
      window.removeEventListener('keydown', onKeyDown);
      menuButtonRef.current?.focus();
    };
  }, [menuOpen]);

  const trapDrawerFocus = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (!menuOpen || event.key !== 'Tab') return;
    const focusable = [...(drawerRef.current?.querySelectorAll<HTMLElement>('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])') ?? [])];
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };
  const navigate = (section: AppSection) => { onNavigate(section); setMenuOpen(false); };

  return <div className="app-shell" data-layout="sidebar-content">
    <div className="skip-links" aria-label="Rychlá navigace"><a href="#main-content">Přejít na hlavní obsah</a><a href="#primary-navigation">Přejít na navigaci</a></div>
    {menuOpen && <button className="mobile-nav-overlay" type="button" aria-label="Zavřít navigaci" onClick={() => setMenuOpen(false)} />}
    <aside id="primary-navigation" ref={drawerRef} className={`sidebar ${menuOpen ? 'is-open' : ''}`} aria-label="Hlavní navigace" onKeyDown={trapDrawerFocus}>
      <div className="brand"><div className="brand-mark"><Ticket /></div><div><strong>Valemato</strong><span>Core alpha</span></div><button className="mobile-menu-close icon-button" type="button" aria-label="Zavřít navigaci" onClick={() => setMenuOpen(false)}><X /></button></div>
      <nav>{navigationGroups.map(([group, sections]) => <div className="sidebar-group" key={group}><span className="sidebar-group-label">{group}</span>{sections.map(section => { const [id, label, Icon] = primaryNavigation.find(([candidate]) => candidate === section)!; return <button data-nav-section={id} className={active === id ? 'active' : ''} key={id} onClick={() => navigate(id)} aria-current={active === id ? 'page' : undefined}><Icon size={18} aria-hidden="true" /><span>{label}</span></button>; })}</div>)}</nav>
      <div className="sidebar-foot"><button type="button"><CircleUserRound size={19} aria-hidden="true" /><span>Ondřej</span></button></div>
    </aside>
    <main id="main-content" tabIndex={-1}><header className="topbar"><button ref={menuButtonRef} className="mobile-menu-toggle icon-button" type="button" aria-label="Otevřít navigaci" aria-controls="primary-navigation" aria-expanded={menuOpen} onClick={() => setMenuOpen(true)}><Menu /></button><div><span className="eyebrow">Decision intelligence platform</span><h1 ref={pageTitleRef} data-page-section={active} tabIndex={-1}>{titles[active]}</h1></div><div className="top-actions"><button className="search-trigger ghost" onClick={onSearch ?? (()=>{})} aria-label="Otevřít globální vyhledávání"><Search size={17}/><span>Hledat</span><kbd>⌘ K</kbd></button><button className="ghost" onClick={() => navigate('providers')}><PlugZap size={17} /> Providers</button><button className="primary" onClick={onNewPurchase}>+ Nový nákup</button></div></header>{children}</main>
  </div>;
}
